import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { ZoomIn, ZoomOut, RotateCcw, Check, X, Info, AlertTriangle } from 'lucide-react';

/**
 * CROP PRESETS
 * - Thumbnail: 1:1 (square) - for covers, avatars
 * - Banner: 2.8:1 (wide) - for hero banners
 * - AdBanner: 14:9 (~1.56:1) - fixed 280×180 output
 * - Cover: 2:3 (portrait) - for series/novel covers
 */
export const CROP_PRESETS = {
  THUMBNAIL: {
    name: 'Thumbnail',
    aspect: 1,
    outputWidth: 600,
    outputHeight: 600,
    recommendedMin: { width: 600, height: 600 },
  },
  BANNER: {
    name: 'Banner',
    aspect: 2.8,
    outputWidth: 1400,
    outputHeight: 500,
    recommendedMin: { width: 1400, height: 500 },
  },
  AD_BANNER: {
    name: 'Ad Banner',
    aspect: 14 / 9,
    outputWidth: 280,
    outputHeight: 180,
    recommendedMin: { width: 560, height: 360 },
  },
  COVER: {
    name: 'Cover',
    aspect: 2 / 3,
    outputWidth: 1200,
    outputHeight: 1800,
    recommendedMin: { width: 800, height: 1200 },
  },
  SQUARE: {
    name: 'Square',
    aspect: 1,
    outputWidth: 800,
    outputHeight: 800,
    recommendedMin: { width: 400, height: 400 },
  },
};

// Helper to create cropped image from canvas
const createCroppedImage = async (imageSrc, pixelCrop, outputWidth, outputHeight) => {
  const image = new Image();
  image.src = imageSrc;
  
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });
  
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  // Set canvas size to output dimensions
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  
  // Draw the cropped area scaled to output size
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight
  );
  
  // Convert canvas to blob
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob);
      },
      'image/jpeg',
      0.92 // Quality
    );
  });
};

/**
 * ImageCropperModal - Reusable crop/zoom/pan component for image uploads
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed (cancel)
 * @param {string} imageSrc - Data URL or object URL of the image to crop
 * @param {File} file - Original file (for name and size info)
 * @param {number} aspect - Aspect ratio (width/height)
 * @param {number} outputWidth - Target output width in pixels
 * @param {number} outputHeight - Target output height in pixels
 * @param {object} recommendedMin - { width, height } minimum recommended source size
 * @param {string} imageType - Type label for display (e.g., "Cover", "Banner")
 * @param {function} onCropped - Callback with the cropped File/Blob
 * @param {string} preset - Optional preset name from CROP_PRESETS
 */
const ImageCropperModal = ({
  isOpen,
  onClose,
  imageSrc: propImageSrc,
  file,
  aspect: propAspect,
  outputWidth: propOutputWidth,
  outputHeight: propOutputHeight,
  recommendedMin: propRecommendedMin,
  imageType = 'Image',
  onCropped,
  preset,
  // Legacy props for backward compatibility
  open,
  onCroppedFileReady,
  minWidth,
  minHeight,
  maxBytes = 5 * 1024 * 1024,
}) => {
  // Handle both old and new prop names for backward compatibility
  const isModalOpen = isOpen ?? open ?? false;
  const handleCropped = onCropped || onCroppedFileReady;
  
  // Apply preset if provided
  const presetConfig = preset ? CROP_PRESETS[preset] : null;
  const aspect = propAspect ?? presetConfig?.aspect ?? 1;
  const outputWidth = propOutputWidth ?? presetConfig?.outputWidth ?? minWidth ?? 800;
  const outputHeight = propOutputHeight ?? presetConfig?.outputHeight ?? minHeight ?? 800;
  const recommendedMin = propRecommendedMin ?? presetConfig?.recommendedMin ?? { width: 400, height: 400 };
  const presetName = presetConfig?.name || imageType;

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // Load image when file or imageSrc changes
  useEffect(() => {
    if (!isModalOpen) return;
    
    let objectUrl = null;
    
    const loadImage = (src) => {
      const img = new Image();
      img.onload = () => {
        setImageSize({ width: img.width, height: img.height });
        
        // Check if image meets recommended minimum (warning only, not blocking)
        if (img.width < recommendedMin.width || img.height < recommendedMin.height) {
          setWarning(
            `Image smaller than recommended (${recommendedMin.width}×${recommendedMin.height}px). ` +
            `Your image: ${img.width}×${img.height}px. Result may appear blurry.`
          );
        } else {
          setWarning(null);
        }
        setError(null);
      };
      img.onerror = () => {
        setError('Failed to load image. Please try a different file.');
      };
      img.src = src;
      setImageSrc(src);
    };
    
    if (propImageSrc) {
      loadImage(propImageSrc);
    } else if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        loadImage(reader.result);
      };
      reader.onerror = () => {
        setError('Failed to read file. Please try again.');
      };
      reader.readAsDataURL(file);
    }
    
    return () => {
      // Cleanup
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setError(null);
      setWarning(null);
      setCroppedAreaPixels(null);
    };
  }, [file, propImageSrc, isModalOpen, recommendedMin.width, recommendedMin.height]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      const croppedBlob = await createCroppedImage(
        imageSrc,
        croppedAreaPixels,
        outputWidth,
        outputHeight
      );
      
      // Check file size
      if (croppedBlob.size > maxBytes) {
        setError(
          `Cropped image is too large (${(croppedBlob.size / 1024 / 1024).toFixed(1)}MB). ` +
          `Maximum allowed: ${(maxBytes / 1024 / 1024).toFixed(0)}MB. Try zooming out.`
        );
        setProcessing(false);
        return;
      }
      
      // Create a File object from the blob
      const fileName = file?.name?.replace(/\.[^/.]+$/, '.jpg') || 'cropped-image.jpg';
      const croppedFile = new File([croppedBlob], fileName, { type: 'image/jpeg' });
      
      handleCropped(croppedFile);
      handleClose();
    } catch (err) {
      console.error('Error cropping image:', err);
      setError('Failed to process image. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleClose = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setError(null);
    setWarning(null);
    setCroppedAreaPixels(null);
    onClose();
  };

  // Calculate aspect ratio display text
  const getAspectText = () => {
    if (aspect === 1) return '1:1 Square';
    if (aspect === 2 / 3) return '2:3 Portrait';
    if (aspect === 2.8) return '2.8:1 Wide Banner';
    if (Math.abs(aspect - 14/9) < 0.01) return '14:9 Ad Banner';
    if (aspect > 2) return `${aspect.toFixed(1)}:1 Wide`;
    if (aspect < 1) return `1:${(1/aspect).toFixed(1)} Tall`;
    return `${aspect.toFixed(2)}:1`;
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent 
        className="bg-zinc-900 border-zinc-700 max-w-3xl w-full flex flex-col max-h-[90vh]" 
        data-testid="image-cropper-modal"
      >
        <DialogHeader className="sticky top-0 bg-zinc-900 z-10 pb-4 border-b border-zinc-800">
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            Crop {presetName}
          </DialogTitle>
          <p className="text-sm text-zinc-400 mt-1">
            Position and zoom your image to fit the {getAspectText()} frame
          </p>
        </DialogHeader>

        <div className="flex-1 py-4 space-y-4 overflow-y-auto">
          {/* Info Box */}
          <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-300">
              <p>Drag to reposition • Scroll or use slider to zoom • Only the area inside the frame will be saved</p>
              <p className="text-blue-400 mt-1">
                Output: {outputWidth}×{outputHeight}px ({getAspectText()})
              </p>
            </div>
          </div>

          {/* Warning Display (not blocking) */}
          {warning && (
            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg" data-testid="cropper-warning">
              <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-300">{warning}</p>
            </div>
          )}

          {/* Error Display (blocking) */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="cropper-error">
              <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Cropper Area */}
          {imageSrc && (
            <div 
              className="relative bg-black rounded-lg overflow-hidden"
              style={{ height: '400px' }}
              data-testid="cropper-area"
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid={true}
                style={{
                  containerStyle: {
                    backgroundColor: '#000',
                  },
                  cropAreaStyle: {
                    border: '2px solid #f97316',
                  },
                }}
              />
            </div>
          )}

          {/* Zoom Controls */}
          {imageSrc && (
            <div className="flex items-center gap-4 px-2">
              <ZoomOut className="w-5 h-5 text-zinc-400" />
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.1}
                onValueChange={(value) => setZoom(value[0])}
                className="flex-1"
                data-testid="zoom-slider"
              />
              <ZoomIn className="w-5 h-5 text-zinc-400" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                data-testid="reset-crop-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
            </div>
          )}

          {/* Image Info */}
          {imageSize.width > 0 && (
            <p className="text-xs text-zinc-500 text-center">
              Original: {imageSize.width}×{imageSize.height}px
              {file && ` • ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`}
            </p>
          )}
        </div>

        <DialogFooter className="sticky bottom-0 bg-zinc-900 pt-4 border-t border-zinc-800 flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="flex-1 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
            disabled={processing}
            data-testid="cancel-crop-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold"
            disabled={processing || !croppedAreaPixels}
            data-testid="crop-continue-btn"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Processing...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Crop &amp; Continue
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropperModal;
